"use client";

import { configSupportsEncryptedSubmission } from "@decibeltrade/sdk";
import { rolloutBucket } from "@decibeltrade/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";

import { ENCRYPTION_DEVICE_ID_KEY } from "@/constants/local-storage-keys";
import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import { useEncryptionConfigQuery } from "@/contexts/queries/encryption-config";
import { useStatsigConfig } from "@/contexts/statsig-config-context";
import { env } from "@/env";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import type { EncryptionNetworkConfig } from "@/lib/statsig-configs";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";

// Changing this reshuffles every device's encryption bucket. Deliberately not
// the Statsig config-name constant: a config rename must not re-bucket devices.
const ENCRYPTION_ROLLOUT_SALT = "encryption";

/**
 * The pure resolver for this device's encryption cohort state — the
 * device-level half of both the write policy ({@link useEncryptionPolicy}) and
 * the footer indicator (`useEncryptionIndicatorState`, which additionally
 * downgrades on per-account gates). Kept side-effect-free and exported so it can
 * be unit-tested directly. Three outcomes:
 *   - `hidden`    device is not in the encryption rollout cohort (killswitch, or
 *                 the network config is disabled, or the device's rollout bucket
 *                 is out) — the feature isn't being offered here, so show nothing.
 *   - `paused`    in the cohort, but the network currently can't encrypt (the
 *                 fullnode exposes no encryption key), so orders go unencrypted.
 *   - `encrypted` in the cohort and the network supports encryption.
 *
 * Precedence, highest first: hard env killswitch → network config disabled →
 * rollout bucket out → network support probe. Cohort membership (killswitch +
 * `enabled` + rollout bucket) is evaluated first and independently of the
 * network probe, so out-of-cohort devices never depend on the probe resolving.
 *
 * The device bucket comes from the shared `rolloutBucket` helper (FNV-1a mod
 * 100), salted per-feature so encryption membership is independent of other
 * rollouts and stable across reloads as the percentage ramps.
 *
 * Rollout granularity is whole-percent: the bucket is an integer 0–99, so
 * `rolloutPercentage` is rounded to the nearest integer before comparison. A
 * fractional value (e.g. 0.5) would otherwise still include all of bucket 0
 * (~1%), silently overshooting sub-1% canaries — rounding makes the effective
 * cohort explicit. Sub-1% canaries are not supported.
 */
export function resolveEncryptionIndicatorState(params: {
  killswitch: boolean;
  networkSupportsEncryption: boolean;
  config: EncryptionNetworkConfig;
  deviceId: string;
  accountAddress?: string;
}): EncryptionIndicatorState {
  if (!isEncryptionAssigned(params)) return "hidden";
  return params.networkSupportsEncryption ? "encrypted" : "paused";
}

/**
 * Rollout arm for this device, independent of network support. A connected
 * wallet in `config.allowedAddresses` is always in the cohort (the killswitch
 * and `enabled` still win); everyone else falls through to the device bucket.
 */
export function isEncryptionAssigned(params: {
  killswitch: boolean;
  config: EncryptionNetworkConfig;
  deviceId: string;
  accountAddress?: string;
}): boolean {
  const { killswitch, config, deviceId, accountAddress } = params;
  if (killswitch || !config.enabled) return false;
  if (config.allowedAddresses.some((addr) => addressesEqual(addr, accountAddress))) return true;
  return rolloutBucket(ENCRYPTION_ROLLOUT_SALT, deviceId) < Math.round(config.rolloutPercentage);
}

export type EncryptionIndicatorState = "hidden" | "paused" | "encrypted";

// Local-dev override: forces this device into the encryption cohort so the
// indicator and encrypted-txn path can be exercised without a live Statsig
// `encryption_config` block for the current network. It only overrides cohort
// membership — whether the indicator reads "encrypted" vs "paused" still comes
// from the real fullnode support probe, so both states remain testable.
const ENCRYPTION_DEV_OVERRIDE_CONFIG: EncryptionNetworkConfig = {
  enabled: true,
  rolloutPercentage: 100,
  allowedAddresses: [],
};

/**
 * The encryption rollout config to actually resolve against: the live Statsig
 * value, unless a local-dev override is in effect. Guarded on `VERCEL_ENV` being
 * unset (local dev only) exactly like the FFT dev override, so the flag can never
 * take effect in preview/production even if it leaks into the environment.
 */
function useEffectiveEncryptionConfig(): EncryptionNetworkConfig {
  const { encryption: serverEncryption } = useStatsigConfig();
  const { data: encryption = serverEncryption } = useEncryptionConfigQuery(serverEncryption);

  const devOverride =
    env.NEXT_PUBLIC_VERCEL_ENV === undefined &&
    (env.NEXT_PUBLIC_ENCRYPTION_LOCAL_OVERRIDE ?? false);

  return devOverride ? ENCRYPTION_DEV_OVERRIDE_CONFIG : encryption;
}

// Stable per-device id: generated once and reused. Independent of wallet so the
// rollout bucket is consistent before and after connect.
function getOrCreateDeviceId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.localStorage.getItem(ENCRYPTION_DEVICE_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    window.localStorage.setItem(ENCRYPTION_DEVICE_ID_KEY, id);
    return id;
  } catch {
    return "";
  }
}

// Cached for the page's lifetime: storage that drops writes would otherwise mint a new id per call.
let deviceIdSnapshot: string | undefined;
function getDeviceIdSnapshot(): string {
  deviceIdSnapshot ??= getOrCreateDeviceId();
  return deviceIdSnapshot;
}

// The device id never changes after creation, so there is nothing to subscribe to.
const subscribeNoop = () => () => undefined;
const getServerDeviceId = () => "";

export const networkSupportsEncryptionQueryKey = () => ["network-supports-encryption"] as const;

/**
 * Probes whether the connected fullnode exposes an `encryption_key` (i.e. can
 * accept encrypted transactions). Node capability is effectively static for a
 * deploy, so this is cached aggressively. Defaults to `false` until resolved so
 * we never pass the encrypted flag before support is confirmed.
 */
export function useNetworkSupportsEncryptionQuery(): {
  supported: boolean;
  /** True once the probe has settled (success or exhausted retries). */
  isResolved: boolean;
} {
  const { data, isFetched } = useQuery({
    queryKey: networkSupportsEncryptionQueryKey(),
    queryFn: async () => {
      const info = await readDexSdk.deps.aptos.getLedgerInfo();
      return !!info.encryption_key;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, err) => {
      const shouldRetry = failureCount < 3;
      if (!shouldRetry) Logger.error(err, { feature: "encryption-support-probe" });
      return shouldRetry;
    },
  });
  return { supported: data ?? false, isResolved: isFetched };
}

/**
 * Whether transactions from this device should be submitted encrypted (the write
 * path). This is the value handed to the SDK as `defaultEncrypted`, so it must
 * account for every gate the SDK's own `encryptionBlocker` applies: the device cohort +
 * fullnode probe ({@link useEncryptionCohortState}) and the gas-station
 * fee-payer requirement (`configSupportsEncryptedSubmission`, shared with the
 * SDK so the two can't drift). Fail-safe: stays `false` until the fullnode
 * support probe resolves, so we never pass the encrypted flag to a node that
 * can't accept it. The SDK's own capability probe remains the authoritative
 * final safety net for non-web callers.
 *
 * This is NOT what the indicator displays — per-account gates (delegated
 * subaccounts and vaults route through the wallet in plaintext) live in
 * `write-dex-sdk-context` and are folded in by `useEncryptionIndicatorState`.
 *
 * `assigned` is the device's rollout arm, reported with transaction telemetry.
 */
export function useEncryptionPolicy(): { shouldEncrypt: boolean; assigned: boolean } {
  const inputs = useEncryptionRolloutInputs();
  const cohortState = useEncryptionCohortState();
  const assigned = useMemo(() => isEncryptionAssigned(inputs), [inputs]);
  return useMemo(
    () => ({
      shouldEncrypt: cohortState === "encrypted" && configSupportsEncryptedSubmission(sdkConfig),
      assigned,
    }),
    [cohortState, assigned],
  );
}

function useEncryptionRolloutInputs() {
  const { enabled, rolloutPercentage, allowedAddresses } = useEffectiveEncryptionConfig();
  const { accountAddress } = useDecibelWallet();
  const deviceId = useSyncExternalStore(subscribeNoop, getDeviceIdSnapshot, getServerDeviceId);
  return useMemo(
    () => ({
      killswitch: env.NEXT_PUBLIC_ENCRYPTION_KILLSWITCH ?? false,
      config: { enabled, rolloutPercentage, allowedAddresses },
      deviceId,
      accountAddress,
    }),
    [enabled, rolloutPercentage, allowedAddresses, deviceId, accountAddress],
  );
}

/**
 * Whether this device is in the encryption rollout cohort, and whether the
 * network can currently encrypt for it. Deliberately says nothing about the
 * connected account's write path. While the network-support probe is still in
 * flight we report `hidden` for in-cohort devices rather than `paused`, so a
 * device that will end up "encrypted" never flashes "paused" on load.
 * Out-of-cohort devices are `hidden` regardless and never wait on the probe.
 */
export function useEncryptionCohortState(): EncryptionIndicatorState {
  const inputs = useEncryptionRolloutInputs();
  const { supported, isResolved } = useNetworkSupportsEncryptionQuery();

  return useMemo(() => {
    const state = resolveEncryptionIndicatorState({
      ...inputs,
      networkSupportsEncryption: supported,
    });
    // In-cohort but the probe hasn't settled: suppress until it does so we don't
    // flash "paused" (supported defaults false) before resolving to "encrypted".
    if (state !== "hidden" && !isResolved) return "hidden";
    return state;
  }, [inputs, supported, isResolved]);
}
